import { useCallback, useEffect, useState } from 'react';
import axios, { AxiosError } from 'axios';
import { format, parseISO } from 'date-fns';
import { IconButton } from './Buttons';
import { IconEyeSearch } from '@tabler/icons-react';
import { Modal, Text } from '@mantine/core';
import DatablockDetailedView from './DatablockDetailedView';
import { DatablockHistory } from '../types/Datablock';

interface DatablockHistoryProps {
  projectKey: string;
  selectedEnv: string;
  datablockKey: string;
  refetchDatablocks: () => void;
}

export const DatablockHistoryView = ({
  projectKey,
  selectedEnv,
  datablockKey,
  refetchDatablocks,
}: DatablockHistoryProps) => {
  const [datablocks, setDatablocks] = useState<DatablockHistory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  let [versionRollbackMessage, setVersionRollbackMessage] = useState<string>('');
  let [viewDetailVersion, setViewDetailVersion] = useState<number>();
  let [isLoading, setIsLoading] = useState<boolean>(false);
  const [envDetailModalOpened, setEnvDetailModalOpened] = useState(false);

  const fetchHistory = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const authToken = localStorage.getItem('authToken');
      const headers = {
        Authorization: 'Bearer ' + authToken,
      };
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/project/${projectKey}/environment/${selectedEnv}/datablock/${datablockKey}/versions`,
        {
          headers,
        }
      );
      const datablockHistory: DatablockHistory[] = response.data.datablocks;
      setDatablocks(datablockHistory);
    } catch (err) {
      setError('Failed to load datablock history');
    } finally {
      setLoading(false);
    }
  }, [projectKey, selectedEnv, datablockKey]);

  useEffect(() => {
    if (datablockKey) {
      fetchHistory();
    }
  }, [datablockKey, fetchHistory]);

  if (loading) return <div>Loading...</div>;
  if (error) return <div className="text-red-500">{error}</div>;

  if (datablocks.length === 0) return <div>No version history found.</div>;

  const handleSetDatablockAsCurrent = async (version: number) => {
    setVersionRollbackMessage('');
    setIsLoading(true);
    try {
      const authToken = localStorage.getItem('authToken');
      const headers = {
        Authorization: 'Bearer ' + authToken,
      };
      await axios.post(
        `${process.env.REACT_APP_API_URL}/datablock/${datablockKey}/set-as-current`,
        {
          project_key: projectKey,
          historic_version: version,
          environment_key: selectedEnv,
        },
        {
          headers: headers,
        }
      );
      fetchHistory();
      refetchDatablocks();
      setIsLoading(false);
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const axiosError = error as AxiosError;
        if (axiosError.code === 'ERR_BAD_REQUEST') {
          // setEnvCreateMessage('Invalid project name');
        } else {
          // setEnvCreateMessage('A network error occured. Please try again later.');
        }
      } else {
        // setEnvCreateMessage('An error occured. Please try again later');
      }
      setIsLoading(false);
    }
  };

  const handleEnvDetailModalToggle = (opened: boolean, version: number) => {
    setViewDetailVersion(version);
    setEnvDetailModalOpened(opened);
  };

  return (
    <div className="mt-2 overflow-y-auto pr-2">
      <Modal
        opened={envDetailModalOpened}
        onClose={() => handleEnvDetailModalToggle(false, 0)}
        title={<Text size="xl">Datablock details</Text>}
        radius="lg"
        size="60%"
      >
        <DatablockDetailedView
          projectKey={projectKey}
          environmentKey={selectedEnv}
          datablockKey={datablockKey}
          version={viewDetailVersion}
        />
      </Modal>

      <div className="flex flex-row h-full w-full rounded-lg mb-5">
        <div className="rounded-tl-lg rounded-bl-lg bg-[#ff9148] text-white p-[8px] h-full w-[60px] flex justify-center">
          Name
        </div>
        <div className="rounded-tr-lg border border-dashed border-slate-300 rounded-br-lg bg-white p-[6px] h-full w-max">
          {datablocks[0].name}
        </div>
      </div>
      <div className="flex flex-row h-fit w-full rounded-lg mb-5">
        <div className="rounded-tl-lg rounded-bl-lg bg-[#547792] text-white p-[8px] h-full w-[60px] flex justify-center">
          Key
        </div>
        <div className="rounded-tr-lg border border-dashed border-slate-300 rounded-br-lg bg-white p-[6px] h-full w-max">
          {datablocks[0].key}
        </div>
      </div>
      {datablocks.map((datablock) => {
        const date = parseISO(datablock.created_at);
        return (
          <div
            key={datablock.key}
            className="w-full flex flex-row justify-between min-h-[50px] items-center border p-3 mb-4 rounded-md shadow-sm"
          >
            <div className="max-w-[200px]">
              <div className="text-sm bg-gray-400 w-fit pl-2 pr-2 text-white rounded-md">
                v{datablock.version}
              </div>
              <div className="text-sm mt-2">
                {datablock.changelog ? (
                  datablock.changelog
                ) : (
                  <div className="text-slate-600">No changelog</div>
                )}
              </div>
              <div className="text-sm text-slate-500 mt-2">{datablock.created_by}</div>
              <div className="text-sm text-slate-500 mt">{format(date, 'PPpp')}</div>
            </div>
            {datablock.status == 'CURRENT' ? (
              <div className="pl-2 pr-2 pt-[2px] bg-green-500 rounded-md w-fit text-white h-[30px]">
                Current
              </div>
            ) : (
              <div className="flex flex-row">
                <IconButton
                  title="Open detailed view"
                  className="h-[30px] mr-2 text-slate-600 hover:text-slate-800"
                  onClick={() => handleEnvDetailModalToggle(true, datablock.version)}
                >
                  <IconEyeSearch size="1.2rem" />
                </IconButton>
                <div
                  className="pl-2 pr-2 pt-[2px] bg-gray-400 hover:bg-gray-600 cursor-pointer rounded-md w-fit text-white h-[30px]"
                  onClick={() => handleSetDatablockAsCurrent(datablock.version)}
                >
                  Set as current
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};
